import { useContext, useState } from 'react'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { Card, Button, TextInput, Toggle } from '../../Common'
import { WorkspaceContext } from '../../../context/workspaceContextCore'
import { sendSlackNotification } from '../../../services/slackService'
import { useNls } from '../../../nls/nlsProvider'

export interface SlackNotificationsPanelProps {
  actorUid?: string
  workspaceId?: string
  connectedChannel?: string
}

export const SlackNotificationsPanel = ({
  workspaceId: propWorkspaceId,
  connectedChannel: initialChannel,
}: SlackNotificationsPanelProps) => {
  const { t } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const defaultWorkspaceId = propWorkspaceId ?? workspaceContext?.activeWorkspaceId ?? ''

  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId)
  const [channelOverride, setChannelOverride] = useState(initialChannel ?? '')
  const [messageText, setMessageText] = useState('Workspace alert: scheduled sync completed successfully.')
  const [taskAlertsEnabled, setTaskAlertsEnabled] = useState(true)
  const [sending, setSending] = useState(false)
  const [feedback, setFeedback] = useState<{ tone: 'good' | 'danger'; text: string } | null>(null)

  const handleSendTest = async () => {
    if (!workspaceId.trim()) {
      setFeedback({ tone: 'danger', text: 'Workspace ID is required to send notification.' })
      return
    }

    setSending(true)
    setFeedback(null)
    try {
      const result = await sendSlackNotification({
        workspaceId: workspaceId.trim(),
        title: 'Timelining System Notification',
        text: messageText.trim() || 'Test notification from Timelining.',
        channelOverride: channelOverride.trim() || undefined,
      })
      setFeedback({
        tone: 'good',
        text: `${t('NLSSlackTestSuccess')} (${result.channel || 'default channel'})`,
      })
    } catch (err) {
      setFeedback({
        tone: 'danger',
        text: err instanceof Error ? err.message : 'Failed to send Slack notification.',
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Card
      variant="outline"
      accent="primary"
      eyebrow="Integrations"
      title={t('NLSSlackPanelTitle')}
      subtitle={t('NLSSlackPanelSubtitle')}
      icon={<NotificationsActiveRoundedIcon fontSize="inherit" />}
    >
      <div className="ownerSettingsRows">
        <label className="ownerSettingsRow">
          <span>
            <strong>{t('NLSSlackTaskNotificationsToggle')}</strong>
            <small>Automatically dispatch task milestones and blocker alerts to Slack.</small>
          </span>
          <Toggle
            checked={taskAlertsEnabled}
            onChange={(enabled) => setTaskAlertsEnabled(enabled)}
            aria-label={t('NLSSlackTaskNotificationsToggle')}
          />
        </label>

        <label className="ownerSettingsRow">
          <span>
            <strong>Workspace ID</strong>
            <small>Target workspace for Slack connection lookup.</small>
          </span>
          <TextInput
            value={workspaceId}
            placeholder="workspace-id"
            onChange={(val) => setWorkspaceId(val)}
            aria-label="Target workspace ID"
          />
        </label>

        <label className="ownerSettingsRow">
          <span>
            <strong>Channel override (optional)</strong>
            <small>Leave empty to use connection default webhook or bot channel.</small>
          </span>
          <TextInput
            value={channelOverride}
            placeholder="#product-updates"
            onChange={(val) => setChannelOverride(val)}
            aria-label="Channel override"
          />
        </label>

        <label className="ownerSettingsRow">
          <span>
            <strong>Notification text</strong>
            <small>Payload message for test dispatch.</small>
          </span>
          <TextInput
            value={messageText}
            placeholder="Test message content..."
            onChange={(val) => setMessageText(val)}
            aria-label="Notification text"
          />
        </label>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
          <Button
            variant="primary"
            size="sm"
            icon={<SendRoundedIcon fontSize="inherit" />}
            loading={sending}
            disabled={sending}
            onClick={() => void handleSendTest()}
          >
            {sending ? t('NLSSlackSendingTest') : t('NLSSlackSendTestMessage')}
          </Button>
        </div>

        {feedback ? (
          <div
            className={`ownerActionResult ownerTone--${feedback.tone}`}
            role="status"
            style={{ marginTop: 8 }}
          >
            <p>{feedback.text}</p>
          </div>
        ) : null}
      </div>
    </Card>
  )
}
